<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="700px"
        transition="slide-y-transition"
        origin="top center"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="isReadyToPrint || showOnly">
                        Towar - <strong>{{ productModel.goodName }}</strong>
                    </template>
                    <template v-else>
                        <template>
                            Edycja ilości opakowań
                        </template>
                    </template>
                </h1>
            </v-card-title>

            <div class="mx-5 my-1">
                <v-row>
                    <v-col cols="12">
                        <v-select
                            v-model="selectedProduct"
                            :items="filteredProducts"
                            label="Produkt"
                            return-object
                            outlined
                            dense
                            item-text="goodName"
                            item-value="id"
                            :disabled="!isEditable || showOnly"
                            :error-messages="productErrors"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            v-model="productModel.goodUnitPackageTypeId"
                            label="Opakowanie"
                            :items="packagesTypes"
                            item-text="name"
                            item-value="id"
                            return-object
                            outlined
                            dense
                            readonly
                            :disabled="!isEditable || showOnly"
                            :error-messages="packagePackageTypeErrors"
                        />
                    </v-col>
                    <v-col v-if="withoutSpecifiedCardbox" cols="12" sm="6">
                        <v-text-field
                            v-model="productModel.goodUnitTargetInPackageSum"
                            label="Ilość sztuk"
                            type="number"
                            suffix="szt."
                            outlined
                            dense
                            persistent-hint
                            :hint="goodUnitTargetInPackageSumInfo"
                            :disabled="isGoodUnitTargetInPackageSumFullAssigned || !isEditable || showOnly"
                            :error-messages="goodUnitTargetInPackageSumErrors"
                        />
                    </v-col>
                    <v-col v-else cols="12" sm="6">
                        <v-text-field
                            v-model="productModel.goodUnitPackageQuantity"
                            label="Ilość opakowań"
                            type="number"
                            suffix="szt."
                            outlined
                            dense
                            persistent-hint
                            :hint="goodUnitPackageQuantityInfo"
                            :disabled="isUnitPackageQuantityFullAssigned || !isEditable || showOnly"
                            :error-messages="goodUnitPackageQuantityErrors"
                        />
                    </v-col>
                </v-row>
            </div>
            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="isEditable && !showOnly"
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :disabled="(filteredProducts.length === 0) || (isProductAdded && isUnitPackageQuantityFullAssigned && isGoodUnitTargetInPackageSumFullAssigned)"
                    @click="onSave"
                >
                    <template>
                        Zapisz
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import packages from '@/mixins/packages'
import { required, integer, between } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import { mask } from 'vue-the-mask'

export default {
    name: 'ShippingLabelProductDialog',
    directives: { mask },
    mixins: [
        packages,
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        isEditable: {
            type: Boolean
        },
        products: {
            type: Array,
            default: () => []
        },
        productData: {
            type: Object,
            default: null
        },
        isReadyToPrint: {
            type: Boolean
        },
        action: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            productModel: {},
            selectedProduct: null,
            selectedPackage: null,
            packagesTypes: []
        }
    },
    validations() {
        let rules = {
            productModel: {
                goodName: {
                    required,
                },
                goodUnitPackageTypeId: {
                    required
                }
            },
        }

        if (this.withoutSpecifiedCardbox) {
            rules.productModel['goodUnitPackageQuantity'] = {
                required,
                between: between(0, this.goodUnitPackageQuantityToAssign),
                integer
            }
            rules.productModel['goodUnitTargetInPackageSum'] = {
                required,
                between: between(1, this.goodUnitTargetInPackageSumToAssign),
                integer
            }
        }
        if (!this.withoutSpecifiedCardbox) {
            rules.productModel['goodUnitPackageQuantity'] = {
                required,
                between: between(1, this.goodUnitPackageQuantityToAssign),
                integer
            }
            rules.productModel['goodUnitTargetInPackageSum'] = {
                required,
                between: between(1, this.goodUnitTargetInPackageSumToAssign),
                integer
            }
        }

        return rules
    },
    computed: {
        showOnly() {
            return this.action === 'show'
        },
        isProductAdded() {
            return this.action === 'add'
        },
        goodUnitPackageQuantityToAssign() {
            const product = this.filteredProducts?.find(item => item.id === this.productModel.id)

            if (!product) return 0


            const addedUnitPackageQuantity = this.action === 'edit' || this.action === 'show'? this.productData?.unitPackageQuantity : 0

            return (product.sourceGoodUnitPackageQuantity - product.assignedAmount + addedUnitPackageQuantity) > 0
                ? product.sourceGoodUnitPackageQuantity - product.assignedAmount + addedUnitPackageQuantity : 0
        },
        goodUnitTargetInPackageSumToAssign() {
            const product = this.filteredProducts?.find(item => item.id === this.productModel.id)

            if (!product) return 0

            const addedAmount = this.action === 'edit' || this.action === 'show'? this.productData?.amount : 0

            return (product.sourceGoodUnitTargetInPackageSum - product.assignedUnitTargetInPackageSumAmount + addedAmount) > 0
                ? product.sourceGoodUnitTargetInPackageSum - product.assignedUnitTargetInPackageSumAmount + addedAmount : 0
        },
        goodUnitTargetInPackageSumInfo() {
            return `${this.productModel.goodUnitTargetInPackageSum} / ${this.goodUnitTargetInPackageSumToAssign}`
        },
        goodUnitPackageQuantityInfo() {
            return `${this.productModel.goodUnitPackageQuantity} / ${this.goodUnitPackageQuantityToAssign}`
        },
        isUnitPackageQuantityFullAssigned() {
            return this.productModel.goodName !== null && this.productModel.goodUnitPackageTypeId !== null && this.goodUnitPackageQuantityToAssign === 0
        },
        isGoodUnitTargetInPackageSumFullAssigned() {
            return this.productModel.goodName !== null && this.productModel.goodUnitPackageTypeId !== null && this.goodUnitTargetInPackageSumToAssign === 0
        },
        withoutSpecifiedCardbox() {
            return this.productModel.goodUnitPackageTypeId === 49
        },
        // Walidacje
        productErrors() {
            const errors = []

            if (!this.$v.productModel.goodName.$dirty) return errors
            !this.$v.productModel.goodName.required && errors.push('Proszę wybrać produkt.')

            return errors
        },
        packagePackageTypeErrors() {
            const errors = []

            if (!this.$v.productModel.goodUnitPackageTypeId.$dirty) return errors
            !this.$v.productModel.goodUnitPackageTypeId.required && errors.push('Proszę wybrać rodzaj opakowania.')

            return errors
        },
        goodUnitPackageQuantityErrors() {
            const errors = []

            if (this.withoutSpecifiedCardbox) return errors

            if (!this.$v.productModel.goodUnitPackageQuantity.$dirty) return errors

            if (!this.$v.productModel.goodUnitPackageQuantity.required) {
                errors.push('Proszę podać ilość')
            }

            if (!this.$v.productModel.goodUnitPackageQuantity.integer) {
                errors.push('Niepoprawny format')
            }

            if (!this.$v.productModel.goodUnitPackageQuantity.required || !this.$v.productModel.goodUnitPackageQuantity.between && !(this.isProductAdded && this.isUnitPackageQuantityFullAssigned)) {
                errors.push(`Proszę podać wartość z zakresu 1-${this.goodUnitPackageQuantityToAssign}`)
            }

            return errors
        },
        goodUnitTargetInPackageSumErrors() {
            const errors = []

            if (!this.withoutSpecifiedCardbox) return errors

            if (!this.$v.productModel.goodUnitTargetInPackageSum.$dirty) return errors

            if (!this.$v.productModel.goodUnitTargetInPackageSum.required) {
                errors.push('Proszę podać ilość')
            }

            if (!this.$v.productModel.goodUnitTargetInPackageSum.integer) {
                errors.push('Niepoprawny format')
            }

            if (!this.$v.productModel.goodUnitTargetInPackageSum.required || !this.$v.productModel.goodUnitTargetInPackageSum.between && !(this.isProductAdded && this.isGoodUnitTargetInPackageSumFullAssigned)) {
                errors.push(`Proszę podać wartość z zakresu 1-${this.goodUnitTargetInPackageSumToAssign}`)
            }

            return errors
        },
        filteredProducts() {
            return this.products
                .filter(item => this.action === 'edit' || this.action === 'show'? item?.id === this.productData?.sourceConfigurationId : item?.id === this.productData?.id)
        },
    },
    watch: {
        async isOpen(value) {
            await this.getPackagesTypes()
            if (value && !isEmpty(this.productData)) {
                this.setInitialData(JSON.parse(JSON.stringify(this.productData)))
                this.setFieldProduct(this.selectedProduct)
            }
        }
    },
    mounted() {
        this.setInitialData()
    },
    methods: {
        packagesTypesFiltered() {
            const products = this.filteredProducts?.filter(item => item.id === this.productModel.id)

            return this.packagesTypes = products.map(item => item.goodUnitPackageType)
        },
        setFieldProduct(product) {
            this.productModel = {...this.productModel, ...product}

            this.productModel.goodUnitPackageQuantity = this.action === 'add'? this.productData?.goodUnitPackageQuantity : this.productData?.unitPackageQuantity

            this.productModel.goodUnitTargetInPackageSum = this.action === 'add' ? this.productData?.goodUnitTargetInPackageSum : this.productData?.amount

            if (!this.withoutSpecifiedCardbox) {
                this.productModel.goodUnitTargetInPackageSum = product.goodUnitTargetInPackageSum
            }

            this.productModel.sourceConfigurationId = product.id

            this.setPackageType(product)
            this.packagesTypesFiltered()
        },
        setPackageType(product) {
            this.productModel.goodUnitPackageTypeId = product.goodUnitPackageTypeId
            this.productModel.goodUnitPackageType = product.goodUnitPackageType
            this.productModel.goodUnitPackageLength = product.goodUnitPackageLength
            this.productModel.goodUnitPackageWidth = product.goodUnitPackageWidth
            this.productModel.goodUnitPackageHeight = product.goodUnitPackageHeight
            this.productModel.goodUnitPackageWeight = product.goodUnitPackageWeight
            this.productModel.maxWeightCapacity = product.maxWeightCapacity

            if (this.withoutSpecifiedCardbox) {
                this.productModel.goodUnitPackageQuantity = 0
            }
        },
        setInitialData(value = {}) {
            this.selectedProduct = !isEmpty(value) ? this.filteredProducts.find(item => this.action === 'edit' || this.action === 'show' ? item.id === value?.sourceConfigurationId :
                item.id === value?.id): null

            this.selectedPackage = !isEmpty(value)
                ? this.packageTypes.find(item => this.action === 'edit' || this.action === 'show' ? item.id === value?.unitPackageTypeId : item.id === value?.goodUnitPackageTypeId)
                : null

            const amount = this.withoutSpecifiedCardbox ? value?.amount : value?.amount * value?.unitInPackageQuantity

            this.productModel = {
                id: value?.sourceConfigurationId || value?.id || null,
                goodUnitTargetInPackageSum: amount || value?.goodUnitTargetInPackageSum || 0,
                goodUnitPackageQuantity: value?.unitPackageQuantity || value?.goodUnitPackageQuantity || 0,
                goodUnitInPackageQuantity: value?.unitInPackageQuantity || value?.goodUnitInPackageQuantity || 0,
                goodUnitPackageTypeId: value?.unitPackageTypeId || value?.goodUnitPackageTypeId || null,
                goodUnitPackageLength: value?.unitPackageLength || value?.goodUnitPackageLength || null,
                goodUnitPackageWidth:  value?.unitPackageWidth || value?.goodUnitPackageWidth || null,
                goodUnitPackageHeight: value?.unitPackageHeight || value?.goodUnitPackageHeight || null,
                goodUnitPackageWeight: value?.unitPackageWeight || value?.goodUnitPackageWeight || null,
                setManuallyAmount: value?.setManuallyAmount || false,
                maxWeightCapacity: value?.maxWeightCapacity || 0.0,
                goodCode: value?.goodCode || null
            }
        },
        setProduct() {
            const amount = this.withoutSpecifiedCardbox ? parseInt(this.productModel.goodUnitTargetInPackageSum) :
                parseInt(this.productModel.goodUnitInPackageQuantity) * parseInt(this.productModel.goodUnitPackageQuantity)

            const addressProduct = {
                amount : amount,
                goodCode: this.productModel.availableGoodCode,
                goodId: this.productModel.availableGoodId,
                good: this.productModel.availableGood,
                goodName: this.productModel.goodName,
                singleItemWeight: this.productModel.singleItemGoodWeight,
                maxWeightCapacity: this.productModel.maxWeightCapacity,
                setManuallyAmount: this.productModel.setManuallyAmount,
                unitPackageHeight: this.productModel.goodUnitPackageHeight,
                unitPackageLength: this.productModel.goodUnitPackageLength,
                unitPackageQuantity: parseInt(this.productModel.goodUnitPackageQuantity),
                unitPackageWeight: this.productModel.goodUnitPackageWeight,
                unitPackageWidth: this.productModel.goodUnitPackageWidth,
                sourceConfigurationId: this.productModel.sourceConfigurationId,
                unitPackageTypeId: this.productModel.goodUnitPackageTypeId,
                unitPackageType: this.productModel.goodUnitPackageType
            }

            return addressProduct
        },
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            if (this.action === 'edit') {
                const productConfiguration = JSON.parse(JSON.stringify(this.productModel))

                const product = this.setProduct()

                this.$emit('on-save', product, productConfiguration)
            } else {
                this.$emit('on-save', JSON.parse(JSON.stringify(this.productModel)))
            }

            this.onClose()
        },
        onClose() {
            this.$emit('on-close')
            setTimeout(() => {
                this.setInitialData()
                this.$v.$reset()
            }, 500)
        }
    }
}
</script>